<template>
    <article>
        <div class="article-content">
            <h2>Contact us</h2>
            <div class="contacts-container">
                <div v-for="contact in contacts" :key="contact.email" class="contact">
                    <img v-if="contact.image" :src="contact.image" :alt="contact.name" class="matt-gibson" />
                    <div v-else class="no-pic"></div>
                    <h3>
                        <a v-if="contact.url" :href="contact.url" target="_blank">{{ contact.name }}</a>
                        <template v-else>{{ contact.name }}</template>
                    </h3>
                    <p><strong>{{ contact.role }}</strong></p>
                    <p><strong><a :href="`mailto:${contact.email}`">{{ contact.email }}</a></strong></p>
                    <p><a :href="`tel:${formatPhoneNumber(contact.telephone)}`">{{ contact.telephone }}</a></p>
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
    import { computed, ref, onMounted } from 'vue';
    import { useRoute } from 'vue-router';
    import { useHead } from '@unhead/vue';
    import axios from 'axios';
    import { BASE_URL, LCP_ORG_URL } from '../config';

    interface Contact {
        name: string;
        role: string;
        email: string;
        telephone: string;
        image: string | null;
        url: string | null;
    }

    const route = useRoute();
    const pageUrl = computed(() => `${BASE_URL}${route.path}`);
    const contacts = ref<Contact[]>([]);

    const formatPhoneNumber = (phone: string) => {
        return phone.replace('(0)', '').replace(/\s+/g, '');
    };

    onMounted(async () => {
        try {
            const response = await axios.get('/data/contacts.json');
            contacts.value = response.data;
        } catch (error) {
            console.error('Failed to load contacts:', error);
        }
    });

    useHead({
        title: 'Contact Us',
        meta: [
            { name: 'description', content: 'Contact the LCP Investment Fee Survey team for inquiries about investment manager fees and asset class data.' },
            { property: 'og:title', content: 'Contact Us - LCP Investment Fee Survey' },
            { property: 'og:description', content: 'Get in touch with the LCP Investment Fee Survey team.' },
            { property: 'og:url', content: pageUrl },
            { property: 'og:image', content: `${BASE_URL}/images/ifs-social.png` },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: 'Contact Us - LCP Investment Fee Survey' },
            { name: 'twitter:description', content: 'Get in touch with the LCP Investment Fee Survey team.' },
            { name: 'twitter:image', content: `${BASE_URL}/images/ifs-social.png` }
        ],
        script: [
            {
                type: 'application/ld+json',
                innerHTML: computed(() => JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "mainEntity": contacts.value.map(contact => ({
                        "@type": "Person",
                        "name": contact.name,
                        "jobTitle": contact.role,
                        "email": contact.email,
                        "telephone": contact.telephone,
                        "image": contact.image ? `${BASE_URL}${contact.image}` : undefined,
                        "affiliation": {
                            "@type": "Organization",
                            "name": "Lane Clark & Peacock LLP",
                            "url": LCP_ORG_URL,
                            "logo": `${LCP_ORG_URL}/media/ipqjtxx3/branding_logo.svg`,
                            "@id": `${LCP_ORG_URL}/#organization`
                        }
                    }))
                }))
            }
        ]
    })
</script>

<style lang="scss" scoped>
    .matt-gibson, .no-pic {
        width: 100%;
        border-radius: 20px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        object-fit: cover;
        background-color: #e5e5e5; /* Placeholder color */
        margin-bottom: 20px;
        display: block; /* Removes bottom space for inline images */
    }

    strong {
        font-weight: 700;
    }

    p {
        margin: 0 0 6px 0;
        line-height: 1.4rem;
        word-break: break-all; /* Ensure emails don't overflow on small screens */
    }

    .article-content {
        padding: 25px;

        .contacts-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 4rem;
            width: 100%;
        }
    }

    .contact {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    h2, h3, li span {
        font-weight: 700;
    }

    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        padding: 0 0 20px 0;
        color: var(--lcp-blue);
        position: relative;
        margin-bottom: 20px;
        display: inline-block;

        &::before {
            content: '';
            position: absolute;
            bottom: 8px;
            left: 0;
            width: calc(100% - 10px);
            height: 3px;
            background: linear-gradient(to right,
                var(--investment-violet) 0%,
                var(--investment-violet) 50%,
                transparent 100%);
            border-radius: 2px;
        }

        &::after {
            content: '';
            position: absolute;
            bottom: 5px;
            left: 0px;
            width: 9px;
            height: 9px;
            background: var(--investment-violet);
            border-radius: 50%;
        }
    }
    h3, h3 a {
        font-size: 1.124rem;
        line-height: 1.6rem;
        margin: 0 0 6px 0;
        flex-grow: 1; /* Pushes content below to the bottom if cards are different heights */
    }

    a, a:active, a:visited {
        color: var(--lcp-blue);
    }
    a:hover {
        color: var(--dark-orange);
    }

    /* Responsive Breakpoints */
    @media only screen and (max-width: 1024px) {
        .article-content {
            .contacts-container {
                grid-template-columns: repeat(2, 1fr);
            }
        }
    }

    @media only screen and (max-width: 600px) {
        .article-content {
            .contacts-container {
                grid-template-columns: 1fr;
            }
        }
    }
</style>
